import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { trackPromise, usePromiseTracker } from "react-promise-tracker";
import { useLocation, useNavigate } from "react-router-dom";
import apiClient from "../../lib/api";
import urlConstant from "../../constants/urlConstants";
import dataConstant from "../../constants/dataConstants";

const ResetPasswordPage = () => {
  const {
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
  });

  const navigate = useNavigate();
  const location = useLocation();

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [token, setToken] = useState(null);
  const [userId, setUserId] = useState(null);
  const [resetPasswordStatus, setResetPasswordStatus] = useState(false);
  const [apiError, setApiError] = useState("");

  const { promiseInProgress } = usePromiseTracker();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    setToken(searchParams.get("token"));
    setUserId(searchParams.get("id"));
  }, [location]);

  const handleToggleVisibilityForNewPassword = () => {
    setShowNewPassword((prev) => !prev);
  };

  const handleToggleVisibilityForConfirmPassword = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  const onSubmit = (data) => {
    if (data.newPassword !== data.confirmPassword) {
      setError("confirmPassword", {
        type: "manual",
        message: "Passwords do not match",
      });
      return;
    }

    const resetPasswordPayload = {
      userId,
      newPassword: data.newPassword,
      token,
    };

    setApiError("");

    trackPromise(
      apiClient
        .post(
          urlConstant.Login.ResetUserPasswordByToken,
          resetPasswordPayload,
          { authorized: false }
        )
        .then((response) => {
          if (response?.status === "Success" && response?.statusCode === 200) {
            setResetPasswordStatus(true);
          } else {
            const errorMessage = (response?.status === "Failure" && response?.statusCode === 500)
              ? "Something went wrong"
              : response?.message
            setApiError(errorMessage);
            setResetPasswordStatus(false);
          }
        })
        .catch((error) => {
          setApiError("Unexpected error occurred");
          setResetPasswordStatus(false);
        })
    );
  };

  return (
    <>
      {resetPasswordStatus ? (
        <Box
          sx={{
            maxWidth: 400,
            mx: "auto",
            mt: 5,
            p: 3,
            boxShadow: 2,
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <Typography variant="h6" gutterBottom>
            Your password has been reset successfully.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
            onClick={() => navigate("/login")}
          >
            Go to Login
          </Button>
        </Box>
      ) : (
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            maxWidth: 400,
            mx: "auto",
            mt: 5,
            p: 3,
            boxShadow: 2,
            borderRadius: 2,
          }}
        >
          <Typography variant="h5" gutterBottom textAlign="center">
            Reset Your Password
          </Typography>

          <Controller
            name="newPassword"
            control={control}
            rules={{
              required: "New password is required",
              pattern: {
                value: dataConstant.passwordRegex,
                message:
                  "Password must be 6-16 characters and include uppercase, lowercase, number, and special character",
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="New Password"
                variant="outlined"
                fullWidth
                margin="normal"
                type={showNewPassword ? "text" : "password"}
                error={!!errors.newPassword}
                helperText={
                  errors.newPassword?.message && (
                    <span style={{ whiteSpace: 'pre-line' }}>
                      {errors.newPassword.message}
                    </span>
                  )
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleToggleVisibilityForNewPassword} edge="end">
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          <Controller
            name="confirmPassword"
            control={control}
            rules={{
              required: "Please confirm your password",
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Confirm Password"
                variant="outlined"
                fullWidth
                margin="normal"
                type={showConfirmPassword ? "text" : "password"}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleToggleVisibilityForConfirmPassword} edge="end">
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          {apiError && (
            <Typography variant="body2" color="error" mt={1}>
              {apiError}
            </Typography>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 2 }}
            disabled={promiseInProgress}
          >
            Reset Password
          </Button>
        </Box>
      )}
    </>
  );
};

export default ResetPasswordPage;